'use client';

import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  PropsWithChildren,
} from 'react';

import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';

import { useTrackWidgetUsage } from './useTrackWidgetUsage';

import 'iframe-resizer/js/iframeResizer.contentWindow';

export type WidgetServiceContextProps = {
  sendMessage: (message: unknown, targetOrigin?: string) => void;
};

const WidgetServiceContext = createContext<
  WidgetServiceContextProps | undefined
>(undefined);

export const useWidgetServiceContext = (): WidgetServiceContextProps => {
  const context = useContext(WidgetServiceContext);
  if (!context) {
    throw new Error('useWidgetServiceContext must be inside a WidgetService');
  }
  return context;
};

type WidgetUpdatePayload = {
  __typename: string;
  id: string | number;
};

type IFrameMessage = {
  type: string;
  payload?: unknown;
};

const WIDGET_UPDATE_FRAGMENT = gql`
  fragment UpdateWidget on widgets_widget {
    category
    title
    name
    widget_teams(order_by: { team: { rank: asc } }) {
      id
      team_detail {
        id
        team_id
        name_in_club
      }
    }
  }
`;

export const WidgetService = ({ children }: PropsWithChildren) => {
  const [isReady, setIsReady] = useState(false);
  const apolloClient = useApolloClient();

  useTrackWidgetUsage();

  useEffect(() => {
    window.iFrameResizer = {
      onMessage: (message: unknown) => {
        const msg = message as IFrameMessage;
        if (msg?.type !== 'WIDGET_UPDATE') {
          return;
        }

        const widget = msg.payload as WidgetUpdatePayload;
        apolloClient.cache.writeFragment({
          id: `${widget.__typename}:${widget.id}`,
          fragment: WIDGET_UPDATE_FRAGMENT,
          data: widget,
        });
      },
      onReady: () => {
        setIsReady(true);
      },
    };
  }, [apolloClient]);

  const sendMessage = useCallback(
    (message: unknown, targetOrigin?: string) => {
      if (!isReady || !window.parentIFrame) return;
      try {
        window.parentIFrame.sendMessage(message, targetOrigin);
      } catch (error) {
        console.warn('Failed to send message to parent iframe:', error);
      }
    },
    [isReady]
  );

  return (
    <WidgetServiceContext.Provider value={{ sendMessage }}>
      {children}
    </WidgetServiceContext.Provider>
  );
};
